import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { focusInView } from '../utils/reveal';
interface ScrollSwapLineProps {
  /** Line shown first */
  from: string;
  /** Line it blurs over to once scrolled past `swapAt` */
  to: string;
  /** Swap once the line's top edge rises to this fraction of the viewport height (default: middle) */
  swapAt?: number;
  /** Phones: swap point override (smaller = later, higher up the screen) */
  mobileSwapAt?: number;
  /** Phones: break the `to` line onto a second line after this word index */
  mobileBreakAfter?: number;
  /** Phones: break the `from` line onto a second line after this word index */
  mobileBreakFromAfter?: number;
}
const SWITCH_EASE = [0.45, 0, 0.25, 1] as const;
/** Per-word delay for the faint left-to-right ripple (s) */
const WORD_STAGGER = 0.035;

/**
 * Centered serif line in the manifesto style ("Most people build brands…"). Comes into focus on scroll,
 * inverts against whatever is behind it, and cross-blurs to a second line once scrolled past `swapAt`
 * (and back again when scrolling up).
 */
export const ScrollSwapLine = forwardRef<HTMLParagraphElement, ScrollSwapLineProps>(function ScrollSwapLine({
  from,
  to,
  swapAt = 0.5,
  mobileSwapAt,
  mobileBreakAfter,
  mobileBreakFromAfter
}, ref) {
  const reduce = useReducedMotion();
  const lineRef = useRef<HTMLParagraphElement>(null);
  useImperativeHandle(ref, () => lineRef.current as HTMLParagraphElement);
  const [swapped, setSwapped] = useState(false);
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const el = lineRef.current;
      if (!el) return;
      const at = mobileSwapAt !== undefined && window.innerWidth < 768 ? mobileSwapAt : swapAt;
      const next = el.getBoundingClientRect().top <= window.innerHeight * at;
      setSwapped(s => s === next ? s : next);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [swapAt, mobileSwapAt]);
  const text = swapped ? to : from;
  return <motion.p ref={lineRef} aria-live="polite"
  // Both lines share one grid cell, so they blur through each other in place (no empty gap)
  className="grid justify-items-center px-3 text-center font-serif font-medium md:font-normal text-[1.375rem] md:text-[clamp(1.125rem,1.75vw,1.75rem)] leading-snug tracking-[-0.01em] text-white mix-blend-difference" {...focusInView(0, reduce, {
    amount: 0.8
  })} data-id="element-186" data-mp="29pq8xrojt9cd">
      {/* Old line drifts out toward the top-right while the new one resolves in from the bottom-left, word by word */}
      <AnimatePresence initial={false} data-id="element-187" data-mp="1gzq2vpib9q7x">
        <motion.span key={text} className="[grid-area:1/1]" exit={reduce ? {
        opacity: 0,
        transition: {
          duration: 0
        }
      } : {
        opacity: 0,
        x: 4,
        y: -4,
        filter: 'blur(6px)',
        transition: {
          duration: 0.7,
          ease: SWITCH_EASE
        }
      }} data-id="element-188" data-mp="1h0stdwbq1qnb">
          {text.split(' ').map((word, i, words) => <motion.span key={`${word}-${i}`} className="inline-block whitespace-pre" initial={reduce ? false : {
          opacity: 0,
          x: -4,
          y: 4,
          filter: 'blur(6px)'
        }} animate={{
          opacity: 1,
          x: 0,
          y: 0,
          filter: 'blur(0px)'
        }} transition={reduce ? {
          duration: 0
        } : {
          duration: 0.8,
          ease: SWITCH_EASE,
          delay: i * WORD_STAGGER
        }} data-id="element-189" data-mp="3nujo707bwinf" data-mpi={i}>
              {i < words.length - 1 ? `${word} ` : word}
            </motion.span>).flatMap((node, i) =>
        // Phones: deliberate line breaks, so each line splits into two well-balanced halves
        i === (swapped ? mobileBreakAfter : mobileBreakFromAfter) ? [node, <br key="mobile-break" className="md:hidden" data-id="element-190" data-mp="p4t6x3apphd5" />] : [node])}
        </motion.span>
      </AnimatePresence>
    </motion.p>;
});